import axios from "axios";
import { MobileNavContext } from "app/mobilenav";
import { useCopilotContext } from "components/copilot/CopilotContext";
import { AuthContext } from "components/lib";
import useWindowResize from "components/hooks/getCurrentWindowWidth";
import { jwtDecode } from "jwt-decode";
import { useContext, useEffect, useState } from "react";

const CrispWidget = () => {
  const [userData, setUserData] = useState(null);
  const authContext = useContext(AuthContext);
  const { isOpen: mobileNavOpen } = useContext(MobileNavContext);
  const { open: copilotOpen } = useCopilotContext();
  const deviceWidth = useWindowResize();
  const isMobile = deviceWidth < 992;

  const planPeriod = (planName) => {
    if (planName?.includes("Yearly")) return "yearly";
    if (planName === "LTD") return "lifetime";
    if (planName === "Trial") return "14 days";
    return "monthly";
  };

  const getUser = async () => {
    try {
      const res = await axios.get("/api/user");
      const d = res?.data?.data;
      const data = {
        name: d?.name,
        email: d?.email,
        account_id: d?.account_id,
        user_id: d?.id,
        plan: d?.plan_name,
        period: planPeriod(d?.plan_name),
        plan_expire_at: d?.plan_expire_at,
        impersonate: `https://run.salesblink.io/api/public/impersonate?id=${d?.id}&account_id=${d?.account_id}`,
      };

      if (d?.plan_name === "Trial") {
        data["trial_extend"] = `https://run.salesblink.io/api/public/trial-extend?id=${d?.id}`;
      }

      setUserData(data);
    } catch (err) {
      console.error("ERROR", err);
      if (
        err?.response?.status === 401 ||
        err?.response?.data?.message ===
        "Account not found! Switch workspace to continue."
      ) {
        localStorage.clear();
      }
    }
  };

  useEffect(() => {
    const isLoggedIn = !!authContext?.user;
    const isClient = authContext?.user?.permission === "client";
    
    if (!isLoggedIn || isClient) return;

    if (!userData) getUser();
  }, [authContext]);

  useEffect(() => {
    if (userData && window.$crisp) {
      window.$crisp.push(["set", "user:email", [userData.email]]);
      window.$crisp.push(["set", "user:nickname", [userData.name]]);
      
      const sessionData = [
        ["plan", userData.plan],
        ["period", userData.period],
        ["plan_expire_at", userData.plan_expire_at],
        ["impersonate", userData.impersonate],
        ["account_id", userData.account_id],
        ["user_id", userData.user_id]
      ];

      if (userData.trial_extend) {
        sessionData.push(["trial_extend", userData.trial_extend]);
      }

      window.$crisp.push(["set", "session:data", [sessionData]]);
    }
  }, [userData]);

  useEffect(() => {
    let localData = null;
    try {
      const localUser = JSON.parse(localStorage.getItem("user"));
      if (localUser) localData = jwtDecode(localUser.token);
    } catch(e) {}

    const getShouldHideByDefault = () => {
      return window.location.pathname.includes("verification")
        ? false
        : (localData === null || authContext?.user?.permission === 'client')
        ? true
        : false;
    };

    const handleHiding = () => {
      if (window.$crisp) {
        const shouldHideByDefault = getShouldHideByDefault();
        if (shouldHideByDefault || (isMobile && (mobileNavOpen || copilotOpen))) {
          window.$crisp.push(["do", "chat:hide"]);
        } else {
          window.$crisp.push(["do", "chat:show"]);
        }
      }
    };

    const handleOpenRequest = () => {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:show"]);
        window.$crisp.push(["do", "chat:open"]);
      }
    };

    // Initial state
    handleHiding();

    // Listen for custom events if other components want to open the chat
    window.addEventListener("crisp:openWidget", handleOpenRequest);
    // Keep compatibility with old charla events if needed, or update them all
    window.addEventListener("charla:openWidget", handleOpenRequest);

    return () => {
      window.removeEventListener("crisp:openWidget", handleOpenRequest);
      window.removeEventListener("charla:openWidget", handleOpenRequest);
    };

  }, [userData, mobileNavOpen, copilotOpen, isMobile, authContext?.user?.permission]);

  return null;
};

export default CrispWidget;
